import { create } from "zustand";
import {
  devtools as devtoolsMiddleware,
  persist,
  createJSONStorage,
} from "zustand/middleware";
import type { StateStorage } from "zustand/middleware";
import type { User } from "../types/auth";

// ---- Remember-me preference (always stored in localStorage so it persists) ----
const REMEMBER_ME_KEY = "dahling-remember-me";

export function setRememberMe(value: boolean): void {
  localStorage.setItem(REMEMBER_ME_KEY, String(value));
}

export function getRememberMe(): boolean {
  return localStorage.getItem(REMEMBER_ME_KEY) !== "false";
}

// Custom storage: routes to localStorage or sessionStorage based on preference
const authStorage: StateStorage = {
  getItem: (name) => {
    // Try localStorage first (remembered), then sessionStorage
    return localStorage.getItem(name) ?? sessionStorage.getItem(name);
  },
  setItem: (name, value) => {
    if (getRememberMe()) {
      localStorage.setItem(name, value);
      sessionStorage.removeItem(name);
    } else {
      sessionStorage.setItem(name, value);
      localStorage.removeItem(name);
    }
  },
  removeItem: (name) => {
    localStorage.removeItem(name);
    sessionStorage.removeItem(name);
  },
};

const devtools = import.meta.env.DEV
  ? devtoolsMiddleware
  : (((fn: unknown) => fn) as typeof devtoolsMiddleware);

interface AuthState {
  user: User | null;
  permissions: string[];
  accessToken: string | null;
  refreshToken: string | null;
  accessTokenExpiresAt: string | null;
  isAuthenticated: boolean;
  setAuth: (
    user: User,
    accessToken: string,
    refreshToken: string,
    accessTokenExpiresAt: string,
    permissions: string[]
  ) => void;
  setTokens: (
    accessToken: string,
    refreshToken: string,
    accessTokenExpiresAt: string
  ) => void;
  setUser: (user: User | null, permissions?: string[]) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set) => ({
        user: null,
        permissions: [],
        accessToken: null,
        refreshToken: null,
        accessTokenExpiresAt: null,
        isAuthenticated: false,
        setAuth: (
          user,
          accessToken,
          refreshToken,
          accessTokenExpiresAt,
          permissions
        ) =>
          set(
            {
              user,
              permissions,
              accessToken,
              refreshToken,
              accessTokenExpiresAt,
              isAuthenticated: true,
            },
            undefined,
            "auth/setAuth"
          ),
        setTokens: (accessToken, refreshToken, accessTokenExpiresAt) =>
          set(
            { accessToken, refreshToken, accessTokenExpiresAt },
            undefined,
            "auth/setTokens"
          ),
        setUser: (user, permissions) =>
          set(
            (prev) => ({
              user,
              permissions: permissions ?? prev.permissions,
              isAuthenticated: user !== null,
            }),
            undefined,
            "auth/setUser"
          ),
        clearAuth: () =>
          set(
            {
              user: null,
              permissions: [],
              accessToken: null,
              refreshToken: null,
              accessTokenExpiresAt: null,
              isAuthenticated: false,
            },
            undefined,
            "auth/clearAuth"
          ),
      }),
      {
        name: "dahling-auth",
        version: 2,
        storage: createJSONStorage(() => authStorage),
        partialize: (state) => ({
          user: state.user,
          permissions: state.permissions,
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          accessTokenExpiresAt: state.accessTokenExpiresAt,
          isAuthenticated: state.isAuthenticated,
        }),
        migrate: (persistedState, version) => {
          // v1 → v2: introduced `permissions` field; backfill empty so first
          // /auth/me call hydrates the real value without a runtime crash.
          if (version < 2) {
            return {
              ...(persistedState as Partial<AuthState>),
              permissions: [],
            } as AuthState;
          }
          return persistedState as AuthState;
        },
      }
    ),
    { name: "AuthStore" }
  )
);

// Multi-tab sync: listen for auth changes from other tabs
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== "dahling-auth") return;

    if (!event.newValue) {
      // Another tab cleared auth — sync logout
      const { isAuthenticated } = useAuthStore.getState();
      if (isAuthenticated) {
        useAuthStore.getState().clearAuth();
      }
    } else {
      // Another tab updated tokens (e.g., refresh) — rehydrate
      useAuthStore.persist.rehydrate();
    }
  });
}
